const API_BASE = "";

export class ApiHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(message);
    this.name = "ApiHttpError";
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function fetchCsrf(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/auth/csrf`, { credentials: "include" });
  const data = (await parseJson(res)) as { csrfToken?: string } | null;
  if (!res.ok || !data?.csrfToken) {
    throw new Error(
      "Не удалось получить CSRF. Запустите бэкенд: npm run dev (Vite + API на порту 3000) или npm run dev:api отдельно.",
    );
  }
  csrfToken = data.csrfToken;
  return data.csrfToken;
}

export type ApiUser = {
  id: number;
  login: string;
  role: "admin" | "editor" | "guest" | "viewer";
  isRegistered?: boolean;
  mustChangePassword?: boolean;
  createdAt?: string;
};

export async function fetchMe(): Promise<{ user: ApiUser | null }> {
  const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
  const data = (await parseJson(res)) as { user: ApiUser | null } | null;
  if (!res.ok) return { user: null };
  return data ?? { user: null };
}

export async function login(login: string, password: string): Promise<{ user: ApiUser; csrfToken?: string }> {
  await fetchCsrf();
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken ?? "",
    },
    body: JSON.stringify({ login, password }),
  });
  const data = (await parseJson(res)) as { user?: ApiUser; error?: string; csrfToken?: string } | null;
  if (!res.ok) {
    throw new Error(data?.error ?? "Ошибка входа");
  }
  if (data?.csrfToken) csrfToken = data.csrfToken;
  if (!data?.user) throw new Error("Нет данных пользователя");
  return { user: data.user, csrfToken: data.csrfToken };
}

export async function register(login: string, password: string): Promise<{ user: ApiUser; csrfToken?: string }> {
  await fetchCsrf();
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken ?? "",
    },
    body: JSON.stringify({ login, password }),
  });
  const data = (await parseJson(res)) as { user?: ApiUser; error?: string; csrfToken?: string } | null;
  if (!res.ok) {
    throw new Error(data?.error ?? "Ошибка регистрации");
  }
  if (data?.csrfToken) csrfToken = data.csrfToken;
  if (!data?.user) throw new Error("Нет данных пользователя");
  return { user: data.user, csrfToken: data.csrfToken };
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  if (!csrfToken) await fetchCsrf();
  const res = await fetch(`${API_BASE}/api/auth/change-password`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken ?? "",
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const data = (await parseJson(res)) as { ok?: true; error?: string } | null;
  if (!res.ok) {
    throw new Error(data?.error ?? `HTTP ${res.status}`);
  }
}

export async function adminResetUserPassword(
  userId: number,
): Promise<{ login: string; temporaryPassword: string }> {
  const data = await apiJson<{
    ok: true;
    user: { id: number; login: string };
    temporaryPassword: string;
  }>(`/api/admin/users/${userId}/reset-password`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return { login: data.user.login, temporaryPassword: data.temporaryPassword };
}

export async function logout(): Promise<void> {
  if (!csrfToken) await fetchCsrf();
  const res = await fetch(`${API_BASE}/api/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken ?? "",
    },
    body: JSON.stringify({}),
  });
  csrfToken = null;
  if (!res.ok) {
    const data = (await parseJson(res)) as { error?: string } | null;
    throw new Error(data?.error ?? "Ошибка выхода");
  }
}

import type { BacklogAttachment, ProjectDocumentVersion } from "@/store/projectStore";

export function projectFileUrl(projectId: string, fileId: string): string {
  return `${API_BASE}/api/workspace/project-files/${encodeURIComponent(projectId)}/${encodeURIComponent(fileId)}`;
}

export async function uploadProjectDocumentFile(projectId: string, file: File): Promise<ProjectDocumentVersion> {
  if (!csrfToken) await fetchCsrf();
  const form = new FormData();
  form.append("projectId", projectId);
  form.append("file", file);
  const res = await fetch(`${API_BASE}/api/workspace/project-files/upload`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken ?? "" },
    body: form,
  });
  const data = (await parseJson(res)) as { version?: ProjectDocumentVersion; error?: string };
  if (!res.ok) {
    throw new Error(data?.error ?? `HTTP ${res.status}`);
  }
  if (!data?.version) {
    throw new Error("Сервер не вернул данные файла");
  }
  return data.version;
}

export async function deleteProjectFileOnServer(projectId: string, fileId: string): Promise<void> {
  await apiJson(`/api/workspace/project-files/${encodeURIComponent(projectId)}/${encodeURIComponent(fileId)}`, {
    method: "DELETE",
  });
}

/**
 * Загрузить файл в глобальный архив «Документы ДИАКа» (на главной под Гантом).
 * Технически использует тот же `project-files/upload`, но с псевдо-projectId
 * `__global__`, который пропускает серверный валидатор `[a-zA-Z0-9_-]+`.
 */
export async function uploadGlobalDocumentFile(file: File): Promise<ProjectDocumentVersion> {
  return uploadProjectDocumentFile("__global__", file);
}

/**
 * Загрузить файл, прикреплённый к канбан-карточке. Возвращает готовый
 * `BacklogAttachment` — можно сразу положить в массив вложений карточки.
 */
export async function uploadBacklogAttachment(
  projectId: string,
  file: File,
): Promise<BacklogAttachment> {
  const v = await uploadProjectDocumentFile(projectId, file);
  return {
    id: v.id,
    name: v.originalName,
    mime: v.mime,
    size: v.size,
    uploadedAt: v.uploadedAt,
    uploadedById: v.uploadedById,
    uploadedByLogin: v.uploadedByLogin,
  };
}

export type AssigneeSuggestion = {
  login: string;
  displayName: string | null;
  isRegistered: boolean;
  userId: number | null;
};

export async function fetchAssigneeSuggestions(
  q: string,
  limit?: number,
): Promise<AssigneeSuggestion[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (limit && limit > 0) params.set("limit", String(limit));
  const data = await apiJson<{ items: AssigneeSuggestion[] }>(
    `/api/me/users-suggest${params.toString() ? `?${params.toString()}` : ""}`,
  );
  return data.items ?? [];
}

export type TaskNotificationStatus =
  | "backlog"
  | "planned"
  | "active"
  | "behind"
  | "done";

export type TaskNotificationKind = "gantt" | "backlog";

export type TaskNotification = {
  id: string;
  projectId: string;
  projectTitle: string;
  taskId: string;
  taskKind: TaskNotificationKind;
  taskTitle: string;
  status: TaskNotificationStatus;
  assigneeRaw: string;
  start: string | null;
  end: string | null;
  isRead: boolean;
  isResolved: boolean;
  readAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function fetchMyNotifications(opts: { includeResolved?: boolean } = {}): Promise<{
  notifications: TaskNotification[];
  unreadCount: number;
}> {
  const params = new URLSearchParams();
  if (opts.includeResolved) params.set("includeResolved", "1");
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return apiJson<{ notifications: TaskNotification[]; unreadCount: number }>(
    `/api/me/notifications${suffix}`,
  );
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiJson<{ ok: true }>(`/api/me/notifications/${encodeURIComponent(id)}/read`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiJson<{ ok: true }>(`/api/me/notifications/read-all`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/**
 * Перенос карточки канбана в другой проект. На сервере атомарно:
 * — переносит карточку между `backlog` обоих проектов с пересчётом kanbanOrder,
 * — физически перемещает файлы вложений между каталогами `data/project-files/`,
 * — пишет ревизию `projects` (откат доступен из «История / откат»).
 * Возвращает фактический id колонки в целевом проекте (если запрошенный
 * не существует — будет первая).
 */
export async function moveBacklogCard(args: {
  fromProjectId: string;
  toProjectId: string;
  cardId: string;
  toColumnId?: string;
}): Promise<{ toColumnId: string }> {
  const data = await apiJson<{ ok: true; toColumnId: string }>(
    `/api/workspace/backlog/move`,
    {
      method: "POST",
      body: JSON.stringify(args),
    },
  );
  return { toColumnId: data.toColumnId };
}

/* ───────────────────── Card operations API ────────────────────────────
   Атомарные операции над карточками канбана и подзадачами. Сервер
   применяет каждую операцию к актуальной версии БД, поэтому одновременная
   работа двух пользователей не приводит к потере данных. */

export async function apiCreateCard(projectId: string, card: { id: string } & Record<string, unknown>) {
  return apiJson<{ ok: true; revisedAt: string; card: unknown }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards`,
    { method: 'POST', body: JSON.stringify(card) },
  );
}

export async function apiPatchCard(
  projectId: string,
  cardId: string,
  patch: Record<string, unknown>,
) {
  return apiJson<{ ok: true; revisedAt: string; card: unknown }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export async function apiDeleteCard(projectId: string, cardId: string) {
  return apiJson<{ ok: true; revisedAt: string }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}`,
    { method: 'DELETE' },
  );
}

export async function apiMoveCard(
  projectId: string,
  cardId: string,
  body: { status: string; kanbanOrder?: number },
) {
  return apiJson<{ ok: true; revisedAt: string; card: unknown }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}/move`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function apiCreateSubtask(
  projectId: string,
  cardId: string,
  subtask: { id: string } & Record<string, unknown>,
) {
  return apiJson<{ ok: true; revisedAt: string; subtask: unknown }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}/subtasks`,
    { method: 'POST', body: JSON.stringify(subtask) },
  );
}

export async function apiPatchSubtask(
  projectId: string,
  cardId: string,
  subtaskId: string,
  patch: Record<string, unknown>,
) {
  return apiJson<{ ok: true; revisedAt: string; subtask: unknown }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}/subtasks/${encodeURIComponent(subtaskId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export async function apiFetchFactTasks(projectId: string) {
  return apiJson<{ ok: true; tasks: Array<{ id: string; title: string }>; reason?: string }>(
    `/api/cards/${encodeURIComponent(projectId)}/fact-tasks`,
  );
}

export async function apiDeleteSubtask(projectId: string, cardId: string, subtaskId: string) {
  return apiJson<{ ok: true; revisedAt: string }>(
    `/api/cards/${encodeURIComponent(projectId)}/cards/${encodeURIComponent(cardId)}/subtasks/${encodeURIComponent(subtaskId)}`,
    { method: 'DELETE' },
  );
}

export async function apiJson<T>(
  path: string,
  init: RequestInit & { skipCsrf?: boolean } = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && !init.skipCsrf) {
    if (!csrfToken) await fetchCsrf();
  }
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  if (init.body && typeof init.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  const data = (await parseJson(res)) as T & { error?: string };
  if (!res.ok) {
    const msg =
      (data as { error?: string; message?: string })?.message ??
      (data as { error?: string })?.error ??
      `HTTP ${res.status}`;
    throw new ApiHttpError(msg, res.status, data);
  }
  return data as T;
}
